import React, { useState } from 'react';
import TailwindNonMachineRelatedComponent, {
  TailwindNonMachineRelatedComponentViewModel
} from 'site/pages/shared/events-and-requests/non-machine-related/non-machine-related-tw';
import TailwindPatternMachines from 'site/pages/shared/events-and-requests/pattern-machines-tw';
import { PatternMachinesViewModel } from 'site/pages/shared/events-and-requests/pattern-machines';
import { EventTypeCategory } from 'stores/configuration/events-workflow/event-types-store';
import { RequestFormStore, RequestFormViewModel } from 'stores/requests/request-form-store';
import { useTranslation } from 'react-i18next';
import TailwindLocationEditor from 'widgets/bussiness/location-editor-tw';
import { connect } from 'redux-scaffolding-ts';
import { guidIsNullOrEmpty } from 'utils/useful-functions';
import TailwindProfessionEditor from 'widgets/bussiness/profession-editor-tw';
import TailwindCategoryFormEditor from 'widgets/bussiness/category-form-editor-tw';
import { Categories } from 'stores/requests/requests-store';
import { NonMachineRelatedComponentViewModel } from 'site/pages/shared/events-and-requests/non-machine-related/non-machine-related';

interface RequestDetailContainerProp {
  request?: RequestFormViewModel;
  readOnly: boolean;
  requestFormStore?: RequestFormStore;
  onChange?: (value: Partial<RequestFormViewModel>) => void;
}

const RequestDetailContainer: React.FC<RequestDetailContainerProp> = ({ request, readOnly, requestFormStore, onChange }) => {
  const { t } = useTranslation();
  const [selectedRequest, setSelectedRequest] = useState<RequestFormViewModel>(request);

  const {
    nmrFunctionalArea,
    nmrFunctionalSubArea,
    nmrTrainingName,
    machineRelated,
    eventTypeCategory,
    eventLocation,
    profession,
    nmrCluster,
    category,
    pattern,
    machines
  } = selectedRequest;

  const patternMachines: PatternMachinesViewModel = { pattern, machines };

  const handleNMRChange = (value: NonMachineRelatedComponentViewModel) => {
    requestFormStore.change({
      nmrCluster: value.cluster,
      nmrFunctionalArea: value.functionalArea,
      nmrFunctionalSubArea: value.functionalSubArea,
      nmrTrainingName: value.trainingName
    });
    const copy = {
      ...selectedRequest,
      ...{
        nmrCluster: value.cluster,
        nmrFunctionalArea: value.functionalArea,
        nmrFunctionalSubArea: value.functionalSubArea,
        nmrTrainingName: value.trainingName
      }
    };
    setSelectedRequest(copy);
    onChange && onChange(copy);
  };

  const handleOnPatternMachinesChange = ({ pattern, machines }: PatternMachinesViewModel) => {
    const requestCopy = { ...selectedRequest };
    setSelectedRequest({ ...requestCopy, pattern, machines });
    requestFormStore.change({ pattern, machines });
    onChange && onChange({ ...requestCopy, pattern, machines });
  };

  const handleOnChange = (property: keyof RequestFormViewModel, value: any) => {
    let change: Partial<RequestFormViewModel> = {};
    change[property as string] = value;
    if (property === 'machineRelated' || property === 'category') {
      change.nmrCluster = null;
      change.nmrFunctionalArea = null;
      change.nmrTrainingName = null;
      change.nmrFunctionalSubArea = null;
    }
    if (property === 'machineRelated' && value) change.category = 'Technical' as any;
    if (property === 'category' && value === 'Functional') change.machineRelated = false;

    requestFormStore.change(change);
    const copy = { ...selectedRequest, ...change };
    setSelectedRequest(copy);
    onChange && onChange(copy);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-700">{t('Location')}</label>
        <TailwindLocationEditor
          readOnly={readOnly}
          className="w-full border border-gray-300 p-2 rounded bg-gray-100"
          nullable
          value={guidIsNullOrEmpty(eventLocation?.id) ? null : eventLocation}
          onChange={value => handleOnChange('eventLocation', value)}
          showCountry={true}
          placeholder={t('Location')}
        />
      </div>
      {(profession || !readOnly) && eventTypeCategory && eventTypeCategory !== EventTypeCategory.Extended && (
        <div>
          <label className="text-sm text-gray-700">{t('Role')}</label>
          <TailwindProfessionEditor
            readOnly={readOnly}
            className="w-full border border-gray-300 p-2 rounded bg-gray-100"
            nullable
            value={guidIsNullOrEmpty(profession?.id) ? null : profession}
            onChange={value => handleOnChange('profession', value)}
            placeholder={t('Role')}
          />
        </div>
      )}
      {eventTypeCategory && eventTypeCategory !== EventTypeCategory.Extended && (
        <div>
          <label className="text-sm text-gray-700">{t('Category')}</label>
          <TailwindCategoryFormEditor
            readOnly={readOnly}
            className={`w-full border border-gray-300 p-2 rounded  ${readOnly ? 'cursor-not-allowed bg-gray-50 text-gray-500' : ''}`}
            value={category ? Categories[Categories[category]] : null}
            onChange={value => handleOnChange('category', value)}
            clearable
            placeholder={t('Category')}
          />
        </div>
      )}
      {eventTypeCategory && eventTypeCategory !== EventTypeCategory.Extended && (
        <div>
          <label className="text-sm text-gray-700 block">{t('Machine Related')}</label>
          <div className="flex gap-4 mt-1">
            <button
              disabled={readOnly}
              className={`px-4 py-1 rounded border ${
                machineRelated
                  ? 'bg-primary-100 border-blue-400'
                  : readOnly
                  ? 'bg-gray-100 border-gray-300'
                  : 'bg-white-100 border-gray-300'
              }`}
              onClick={() => handleOnChange('machineRelated', true)}
            >
              {t('Machine Related')}
            </button>
            <button
              disabled={readOnly}
              className={`px-4 py-1 rounded border ${
                !machineRelated
                  ? 'bg-primary-100 border-blue-400'
                  : readOnly
                  ? 'bg-gray-100 border-gray-300'
                  : 'bg-white-100 border-gray-300'
              }`}
              onClick={() => handleOnChange('machineRelated', false)}
            >
              {t('Non-Machine Related')}
            </button>
          </div>
        </div>
      )}
      {eventTypeCategory && eventTypeCategory !== EventTypeCategory.Extended && (
        <div>
          <div className="form__event-details-tab__mr__pattern form__event-details-tab__element mr-table-component__common-styles">
            {machineRelated && (
              <TailwindPatternMachines
                readOnly={readOnly}
                value={patternMachines}
                onChange={handleOnPatternMachinesChange}
                profession={profession}
              />
            )}
          </div>

          {!machineRelated && (
            <TailwindNonMachineRelatedComponent
              category={category}
              value={
                {
                  cluster: nmrCluster,
                  functionalArea: nmrFunctionalArea,
                  functionalSubArea: nmrFunctionalSubArea,
                  trainingName: nmrTrainingName
                } as TailwindNonMachineRelatedComponentViewModel
              }
              readOnly={readOnly}
              onChange={handleNMRChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default connect(['requestFormStore', RequestFormStore])(RequestDetailContainer);
